import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@tabipla/ui";
import { SpotForm } from "./SpotForm";
import { useCreateSpot } from "../../application/hooks/useSpots";
import { showErrorToast } from "../../application/utils/error-toast";

interface AddSpotDialogProps {
  tripId: string;
  itineraryId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destinationCoords?: { latitude: number; longitude: number } | null;
  defaultStartTime?: string | null;
}

export function AddSpotDialog({
  tripId,
  itineraryId,
  open,
  onOpenChange,
  destinationCoords,
  defaultStartTime,
}: AddSpotDialogProps) {
  const { t } = useTranslation();
  const createSpot = useCreateSpot(tripId, itineraryId);

  const handleSubmit = async (data: {
    name: string;
    description: string | null;
    category: "sightseeing" | "food" | "activity" | "transport" | "accommodation";
    startTime: string | null;
    endTime: string | null;
    location: string | null;
    placeId: string | null;
    latitude: number | null;
    longitude: number | null;
    formattedAddress: string | null;
    cost: number | null;
    notes: string | null;
  }) => {
    try {
      await createSpot.mutateAsync({
        name: data.name,
        description: data.description,
        category: data.category,
        startTime: data.startTime,
        endTime: data.endTime,
        location: data.location,
        placeId: data.placeId,
        latitude: data.latitude,
        longitude: data.longitude,
        formattedAddress: data.formattedAddress,
        cost: data.cost,
        notes: data.notes,
      });
      toast.success(t("spots.createSuccess", "スポットを追加しました"));
      onOpenChange(false);
    } catch (error) {
      showErrorToast(error, t);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader className="pb-2">
          <DialogTitle>{t("spots.addSpot")}</DialogTitle>
          <DialogDescription className="sr-only">{t("spots.addSpot")}</DialogDescription>
        </DialogHeader>
        <SpotForm
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isSubmitting={createSpot.isPending}
          tripId={tripId}
          destinationCoords={destinationCoords}
          defaultStartTime={defaultStartTime}
        />
      </DialogContent>
    </Dialog>
  );
}
